import { default as React, useState } from 'react';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

const ORG_MAPPING: Record<string, string> = {
  'wsky-test': 'wst',
  mediwareinc: 'minc',
};

// Must mirror extractAppName() in plugins/scaffolder-backend-module-quick-projects/src/utils/github.ts
function toAppName(repoUrl: string): string {
  try {
    const normalized = repoUrl.trim().startsWith('git@')
      ? repoUrl.trim().replace(/^git@github\.com:/, 'https://github.com/')
      : repoUrl.trim();
    const url = new URL(normalized);
    const parts = url.pathname
      .replace(/^\//, '')
      .replace(/\.git$/, '')
      .split('/');
    if (parts.length < 2 || !parts[0] || !parts[1]) return '';

    const org = parts[0].toLowerCase();
    const mappedOrg = ORG_MAPPING[org] || org;

    let appPart: string;
    if (
      parts.length >= 4 &&
      (parts[2] === 'tree' || parts[2] === 'blob') &&
      parts.length > 4
    ) {
      appPart = parts[parts.length - 1].toLowerCase();
    } else {
      appPart = parts[1].toLowerCase();
    }

    return `${appPart}-${mappedOrg}`
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/^-+|-+$/g, '')
      .replace(/--+/g, '-')
      .slice(0, 63);
  } catch {
    return '';
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
      <IconButton size="small" onClick={handleCopy} aria-label="Copy URL">
        {copied ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
}

function UrlRow({ label, url }: { label: string; url: string }) {
  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography
          variant="body2"
          sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
        >
          {url}
        </Typography>
        <CopyButton text={url} />
      </Box>
    </Box>
  );
}

export const McpCallbackUrlDisplay = ({
  uiSchema,
  formContext,
}: FieldExtensionComponentProps<string>) => {
  const configApi = useApi(configApiRef);
  const domain =
    configApi.getOptionalString('app.mcpDomain') ??
    (uiSchema?.['ui:options']?.domain as string | undefined) ??
    'skystage.wellsky.cloud';

  // Read the GitHub repo URL from the sibling field in the same form step.
  const githubRepoUrl =
    (formContext?.formData?.githubRepoUrl?.value as string | undefined) ?? '';

  const appName = toAppName(githubRepoUrl);
  if (!appName) return null;

  const appUrl = `https://${appName}-mcp.${domain}`;
  // The auth sidecar (v4+) owns /oauth/callback/:provider internally.
  // Every provider callback MUST use this format.
  const callbackPattern = `${appUrl}/oauth/callback/{provider}`;

  return (
    <Alert severity="info" sx={{ mt: 1 }}>
      <Typography variant="body2" gutterBottom>
        Register these URLs in your OAuth app settings{' '}
        <strong>before submitting</strong> this form:
      </Typography>

      <UrlRow label="App URL" url={appUrl} />

      <UrlRow
        label="OAuth Callback URL — replace {provider} with the lowercase provider name (e.g. github, atlassian, slack)"
        url={callbackPattern}
      />

      <Typography variant="caption" color="text.secondary">
        Example provider names: <code>github</code> →{' '}
        <code>/oauth/callback/github</code>
        &nbsp;&nbsp;<code>atlassian</code> →{' '}
        <code>/oauth/callback/atlassian</code>
      </Typography>
    </Alert>
  );
};

export const McpCallbackUrlDisplayExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'McpCallbackUrlDisplay',
    component: McpCallbackUrlDisplay,
  }),
);
